import { computed, Injectable, signal } from '@angular/core';

import { VOICE_CONVERSATION_STATE, VoiceConversationEvent, VoiceQueueRow } from '../voice.types';

/** Local "now" in the server's timestamp convention (naive UTC, no suffix) so
 *  synthetic transitions compare consistently with real STOMP frames. */
const serverNow = () => new Date().toISOString().replace('Z', '');

/**
 * Longest wait a queue row can plausibly represent. The backend's max_wait_sec tops out
 * around 300s, so a row waiting far beyond that is a stale server-side entry for a call
 * of unknown state (its timeout never fired / its terminal event was lost). Ingesting it
 * would ring a ghost forever and keep the 1s timers alive — ignore it instead, and let
 * the hydrate cleanup drop any local entry for it.
 */
const MAX_PLAUSIBLE_WAIT_SEC = 600;

/** Server timestamps may arrive without a timezone suffix (naive UTC) — parse as UTC. */
const parseServerTs = (ts: string): number => {
    const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(ts);
    return Date.parse(hasTz ? ts : ts + 'Z');
};

/**
 * Holds the live human-transfer state of voice conversations, keyed by
 * `conversation_id`, fed by {@link VoiceConversationWebsocketService}. The inbox reads
 * it to blink the waiting conversations and offer "pick up".
 */
@Injectable({ providedIn: 'root' })
export class VoiceConversationStore {
    private readonly _events = signal<Record<number, VoiceConversationEvent>>({});
    readonly events = this._events.asReadonly();

    /** How many conversations are currently waiting for an agent. */
    readonly waitingCount = computed(
        () =>
            Object.values(this._events()).filter(
                e => e.state === VOICE_CONVERSATION_STATE.WAITING_AGENT
            ).length
    );

    /**
     * Apply a STOMP event by upserting it under its `conversation_id`. ENDED is kept (not
     * dropped) so consumers can react to a hang-up immediately — flipping the badge/composer
     * to "call ended" without waiting for the persistent session refresh. The next call on the
     * same (reused) conversation overwrites it with STARTED.
     *
     * Queue cleanup contract: PICKED_UP, QUEUE_TIMEOUT and ENDED (caller hung up while
     * waiting) must all clear the waiting row. The upsert covers the same-conversation case;
     * additionally correlate by `call_control_id` so a terminal event keyed under a different
     * conversation can't leave a ghost row ringing in the queue widget.
     */
    apply(event: VoiceConversationEvent) {
        this._events.update(map => {
            // Out-of-order guard: a late WAITING_AGENT frame for a call we already saw
            // leave the queue (picked up / timed out / ended, incl. our own synthetic
            // transitions) must not resurrect the row. Same call only — a new call on a
            // reused conversation carries a different call_control_id and passes through.
            const existing = map[event.conversation_id];
            const existingLeftQueue =
                existing?.state === VOICE_CONVERSATION_STATE.PICKED_UP ||
                existing?.state === VOICE_CONVERSATION_STATE.QUEUE_TIMEOUT ||
                existing?.state === VOICE_CONVERSATION_STATE.ENDED;
            if (
                event.state === VOICE_CONVERSATION_STATE.WAITING_AGENT &&
                existingLeftQueue &&
                existing.call_control_id === event.call_control_id
            ) {
                return map;
            }
            const next = { ...map, [event.conversation_id]: event };
            const terminal =
                event.state === VOICE_CONVERSATION_STATE.PICKED_UP ||
                event.state === VOICE_CONVERSATION_STATE.QUEUE_TIMEOUT ||
                event.state === VOICE_CONVERSATION_STATE.ENDED;
            if (terminal && event.call_control_id) {
                for (const [id, ev] of Object.entries(next)) {
                    if (
                        Number(id) !== event.conversation_id &&
                        ev.state === VOICE_CONVERSATION_STATE.WAITING_AGENT &&
                        ev.call_control_id === event.call_control_id
                    ) {
                        delete next[Number(id)];
                    }
                }
            }
            return next;
        });
    }

    /** Non-reactive read; prefer a `computed` over `events()` in components. */
    stateFor(conversationId: number): VoiceConversationEvent | undefined {
        return this._events()[conversationId];
    }

    /**
     * Locally flip a conversation's live state to ENDED — used when OUR WebRTC leg hangs
     * up, since a picked-up call never gets an ENDED frame over STOMP (it continues on
     * another leg). Without this the composer keeps saying "call in progress".
     * Timestamp mimics the server convention (naive UTC, no suffix) so the composer's
     * staleness comparisons stay consistent.
     */
    markEnded(conversationId: number) {
        this._events.update(map => {
            const existing = map[conversationId];
            if (!existing || existing.state === VOICE_CONVERSATION_STATE.ENDED) return map;
            return {
                ...map,
                [conversationId]: {
                    ...existing,
                    state: VOICE_CONVERSATION_STATE.ENDED,
                    timestamp: serverNow(),
                },
            };
        });
    }

    /**
     * Locally record a successful pickup so the queue row drops immediately — the
     * PICKED_UP frame may lag (or be missed), and until it lands the widget would keep
     * offering a call that is already bridged. Goes through apply() so the same
     * call_control_id correlation clears any duplicate waiting entry.
     */
    markPickedUp(event: VoiceConversationEvent, agentUserId: number | null) {
        this.apply({
            ...event,
            state: VOICE_CONVERSATION_STATE.PICKED_UP,
            agent_user_id: agentUserId ?? event.agent_user_id,
            timestamp: serverNow(),
        });
    }

    /**
     * Drop a waiting row the server just disowned (pickup 409 "Call is not waiting for
     * an agent"): whatever the reason — picked by someone else, expired, abandoned — the
     * row must go NOW, without depending on the follow-up GET /voice/queue round-trip.
     * Deleting (rather than marking ENDED) lets the persistent session state drive the
     * UI, since a 409 doesn't tell us whether the call is over or live with another agent.
     */
    dropWaiting(conversationId: number) {
        this._events.update(map => {
            if (map[conversationId]?.state !== VOICE_CONVERSATION_STATE.WAITING_AGENT) return map;
            const next = { ...map };
            delete next[conversationId];
            return next;
        });
    }

    /**
     * Reconcile the waiting set with GET /voice/queue — the server is the truth. Rows not
     * in the store become synthetic WAITING_AGENT entries; WAITING_AGENT entries the server
     * no longer lists are dropped (picked up / expired / hung up while the tab was away).
     * Non-waiting states (STARTED, PICKED_UP, ENDED…) are left untouched.
     *
     * `queued_at` is rebased from the server's `wait_sec` onto the local clock, so wait
     * timers stay correct even if the machine's clock drifts from the server's.
     */
    hydrateQueue(rows: VoiceQueueRow[]) {
        // A row already waiting longer than any configurable timeout is a stale backend
        // entry (call state unknown) — treat it as not listed, so it can't (re)create a
        // ghost row and the cleanup below drops any local entry it had seeded.
        rows = rows.filter(r => r.wait_sec <= MAX_PLAUSIBLE_WAIT_SEC);
        this._events.update(map => {
            const next = { ...map };
            for (const [id, ev] of Object.entries(next)) {
                // Match by conversation_id OR call_control_id: the queue endpoint has been
                // seen listing the same call under a different conversation key than the
                // STOMP frame — the live entry must survive as long as the CALL is listed,
                // or the preview's "incoming call" marking vanishes while it still rings.
                if (
                    ev.state === VOICE_CONVERSATION_STATE.WAITING_AGENT &&
                    !rows.some(
                        r =>
                            r.conversation_id === Number(id) ||
                            r.call_control_id === ev.call_control_id
                    )
                ) {
                    delete next[Number(id)];
                }
            }
            for (const row of rows) {
                // A live terminal event for this same call wins over a (possibly stale)
                // queue snapshot — don't resurrect a row STOMP already closed. A different
                // call_control_id means a NEW call on a reused conversation: hydrate it.
                // (STARTED doesn't block: the WAITING frame may simply have been missed.)
                const existing = next[row.conversation_id];
                const existingIsTerminal =
                    existing?.state === VOICE_CONVERSATION_STATE.PICKED_UP ||
                    existing?.state === VOICE_CONVERSATION_STATE.QUEUE_TIMEOUT ||
                    existing?.state === VOICE_CONVERSATION_STATE.ENDED;
                if (existingIsTerminal && existing.call_control_id === row.call_control_id) {
                    continue;
                }
                // The same CALL already waits under another conversation key (STOMP frame,
                // richer/authoritative) — don't create a duplicate row for it.
                const alreadyWaiting = Object.entries(next).some(
                    ([id, ev]) =>
                        Number(id) !== row.conversation_id &&
                        ev.state === VOICE_CONVERSATION_STATE.WAITING_AGENT &&
                        ev.call_control_id === row.call_control_id
                );
                if (alreadyWaiting) continue;
                const queuedAtLocal = new Date(Date.now() - row.wait_sec * 1000).toISOString();
                next[row.conversation_id] = {
                    account_id: next[row.conversation_id]?.account_id ?? 0,
                    conversation_id: row.conversation_id,
                    call_control_id: row.call_control_id,
                    state: VOICE_CONVERSATION_STATE.WAITING_AGENT,
                    agent_user_id: null,
                    timestamp: row.queued_at,
                    queued_at: queuedAtLocal,
                    from: row.from,
                    target_user_id: row.target_user_id ?? null,
                    provider: row.provider ?? next[row.conversation_id]?.provider ?? null,
                    // KAN-238: outbound rows are auto-picked by their originator, never rung.
                    direction: row.direction ?? next[row.conversation_id]?.direction ?? null,
                    to: row.to ?? next[row.conversation_id]?.to ?? null,
                };
            }
            return next;
        });
    }

    /**
     * Drop WAITING_AGENT rows older than any plausible queue wait — the last line of
     * defense against a ghost of unknown state when the periodic re-sync can't clean it
     * (e.g. GET /voice/queue failing silently). Called by the widget's re-sync timer.
     */
    purgeStaleWaiting() {
        const cutoff = Date.now() - MAX_PLAUSIBLE_WAIT_SEC * 1000;
        this._events.update(map => {
            const stale = Object.entries(map).filter(
                ([, ev]) =>
                    ev.state === VOICE_CONVERSATION_STATE.WAITING_AGENT &&
                    parseServerTs(ev.queued_at ?? ev.timestamp) < cutoff
            );
            if (!stale.length) return map;
            const next = { ...map };
            for (const [id] of stale) delete next[Number(id)];
            return next;
        });
    }

    clear() {
        this._events.set({});
    }
}
